import { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePlayer } from '@/hooks/usePlayer'

// ─── Canvas & Visual Constants ────────────────────────────────────────────────
const W = 640
const H = 480
const BG = '#04000a'
const CYAN = '#00e5ff'
const PINK = '#ff2680'
const GREEN = '#39ff14'
const GOLD = '#ffd700'
const PURPLE = '#bf00ff'
const RED = '#ff3030'
const MONO = "'JetBrains Mono','Courier New',monospace"

// ─── Game Constants ───────────────────────────────────────────────────────────
const PARROT_SPEED = 3.2
const PARROT_R = 18
const BULLET_SPEED = 9
const BULLET_CD = 14
const PARROT_MAX_HP = 5
const ZNAKE_SEGMENT_R = 9
const ZNAKE_SEGMENT_GAP = 14
const DAMAGE_FLASH = 25
const INVINCIBLE_FRAMES = 60

// ─── Types ────────────────────────────────────────────────────────────────────
type Scene = 'menu' | 'play' | 'wave_clear' | 'game_over'
interface Vec2 { x: number; y: number }
interface Bullet { id: number; x: number; y: number; vx: number; vy: number; life: number }
interface ZnakeSegment extends Vec2 {}
type ZnakeType = 'crawler' | 'spitter' | 'charger' | 'boss'
interface Znake {
  id: number; type: ZnakeType; segments: ZnakeSegment[]
  hp: number; maxHp: number; speed: number; angle: number; turnSpeed: number
  spitCd: number; spitTimer: number; chargeTimer: number; charging: boolean
  chargeVx: number; chargeVy: number; flashTimer: number; dead: boolean; value: number
}
interface EnemyBullet { id: number; x: number; y: number; vx: number; vy: number }
interface Particle { x: number; y: number; vx: number; vy: number; life: number; decay: number; col: string; size: number; type: 'spark' | 'smoke' | 'feather' }
interface FloatingText { x: number; y: number; text: string; life: number; col: string; vy: number }
interface GameState {
  scene: Scene; tick: number; score: number; best: number; wave: number; waveClearTimer: number
  px: number; py: number; pvx: number; pvy: number; hp: number; invTimer: number
  damageFlash: number; bulletCd: number; facing: Vec2; wingT: number
  bullets: Bullet[]; enemyBullets: EnemyBullet[]; bulletId: number
  znakes: Znake[]; znakeId: number
  particles: Particle[]; floatingTexts: FloatingText[]
  keys: Record<string, boolean>
  submitted: boolean  // prevent double submit per run
}

function rand(a: number, b: number) { return a + Math.random() * (b - a) }
function lerp(a: number, b: number, t: number) { return a + (b - a) * t }
function dist(ax: number, ay: number, bx: number, by: number) { const dx = ax - bx, dy = ay - by; return Math.sqrt(dx * dx + dy * dy) }
function angleTo(ax: number, ay: number, bx: number, by: number) { return Math.atan2(by - ay, bx - ax) }

interface ZnakeSpec { type: ZnakeType; count: number }
function waveSpec(wave: number): ZnakeSpec[] {
  if (wave === 1) return [{ type: 'crawler', count: 4 }]
  if (wave === 2) return [{ type: 'crawler', count: 5 }, { type: 'spitter', count: 1 }]
  if (wave === 3) return [{ type: 'crawler', count: 4 }, { type: 'spitter', count: 2 }, { type: 'charger', count: 1 }]
  if (wave === 4) return [{ type: 'crawler', count: 3 }, { type: 'spitter', count: 2 }, { type: 'charger', count: 2 }]
  if (wave === 5) return [{ type: 'boss', count: 1 }, { type: 'crawler', count: 3 }]
  const extra = wave - 5
  return [
    { type: 'crawler', count: 3 + extra },
    { type: 'spitter', count: 2 + Math.floor(extra / 2) },
    { type: 'charger', count: 1 + Math.floor(extra / 2) },
    { type: 'boss', count: Math.floor(extra / 3) + 1 },
  ]
}
function znakeStats(type: ZnakeType, wave: number): Pick<Znake, 'hp' | 'maxHp' | 'speed' | 'turnSpeed' | 'spitCd' | 'value'> {
  const s = 1 + (wave - 1) * 0.12
  switch (type) {
    case 'crawler': return { hp: 2, maxHp: 2, speed: 0.9 * s, turnSpeed: 0.045, spitCd: 999, value: 10 }
    case 'spitter':  return { hp: 3, maxHp: 3, speed: 0.6 * s, turnSpeed: 0.03,  spitCd: 110, value: 25 }
    case 'charger':  return { hp: 4, maxHp: 4, speed: 0.7 * s, turnSpeed: 0.025, spitCd: 999, value: 30 }
    case 'boss':     return { hp: 20 + wave * 4, maxHp: 20 + wave * 4, speed: 0.55 * s, turnSpeed: 0.018, spitCd: 70, value: 200 }
  }
}
function znakeColor(type: ZnakeType): string {
  switch (type) {
    case 'crawler': return GREEN; case 'spitter': return PURPLE
    case 'charger': return RED; case 'boss': return '#ff8800'
  }
}
function znakeLength(type: ZnakeType): number {
  switch (type) { case 'crawler': return 6; case 'spitter': return 7; case 'charger': return 5; case 'boss': return 14 }
}
function spawnZnake(id: number, type: ZnakeType, wave: number): Znake {
  const side = Math.floor(Math.random() * 4)
  let hx: number, hy: number
  if (side === 0) { hx = rand(0, W); hy = -30 }
  else if (side === 1) { hx = W + 30; hy = rand(0, H) }
  else if (side === 2) { hx = rand(0, W); hy = H + 30 }
  else { hx = -30; hy = rand(0, H) }
  const stats = znakeStats(type, wave)
  const len = znakeLength(type)
  const segs: ZnakeSegment[] = []
  for (let i = 0; i < len; i++) segs.push({ x: hx, y: hy })
  return { id, type, segments: segs, ...stats, angle: Math.atan2(H / 2 - hy, W / 2 - hx), spitTimer: Math.floor(rand(30, stats.spitCd)), chargeTimer: 180, charging: false, chargeVx: 0, chargeVy: 0, flashTimer: 0, dead: false }
}
function makeInitialState(): GameState {
  return {
    scene: 'menu', tick: 0, score: 0, best: 0, wave: 1, waveClearTimer: 0,
    px: W / 2, py: H / 2, pvx: 0, pvy: 0, hp: PARROT_MAX_HP,
    invTimer: 0, damageFlash: 0, bulletCd: 0, facing: { x: 1, y: 0 }, wingT: 0,
    bullets: [], enemyBullets: [], bulletId: 0, znakes: [], znakeId: 0,
    particles: [], floatingTexts: [], keys: {}, submitted: false,
  }
}
function spawnWave(wave: number, startId: number): Znake[] {
  const spec = waveSpec(wave); const znakes: Znake[] = []; let id = startId
  for (const { type, count } of spec) for (let i = 0; i < count; i++) znakes.push(spawnZnake(id++, type, wave))
  return znakes
}
function startGame(prev: GameState): GameState {
  const s = makeInitialState(); s.best = prev.best; s.scene = 'play'; s.wave = 1
  s.znakes = spawnWave(1, 0); s.znakeId = s.znakes.length; s.submitted = false
  return s
}

function burst(particles: Particle[], x: number, y: number, col: string, n: number, type: Particle['type'] = 'spark') {
  for (let i = 0; i < n; i++) {
    const a = Math.random() * Math.PI * 2, sp = type === 'feather' ? rand(1, 5) : rand(2, 6)
    particles.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - (type === 'feather' ? 1.5 : 0), life: 1, decay: 0.02 + Math.random() * 0.025, col, size: type === 'smoke' ? rand(4, 8) : rand(2, 4), type })
  }
}

function drawParrot(ctx: CanvasRenderingContext2D, x: number, y: number, facingLeft: boolean, wingT: number, flashAlpha: number, hp: number) {
  ctx.save(); ctx.translate(x, y)
  if (facingLeft) ctx.scale(-1, 1)
  if (flashAlpha > 0) { ctx.globalAlpha = flashAlpha * 0.35; ctx.fillStyle = PINK; ctx.beginPath(); ctx.arc(0, 0, PARROT_R + 8, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1 }
  ctx.fillStyle = 'rgba(0,229,255,0.07)'; ctx.beginPath(); ctx.ellipse(2, PARROT_R + 4, PARROT_R, 5, 0, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#0088bb'; ctx.beginPath(); ctx.ellipse(-PARROT_R, 4, 10, 5, Math.PI * 0.25, 0, Math.PI * 2); ctx.fill()
  ctx.beginPath(); ctx.ellipse(-PARROT_R - 2, -1, 8, 4, Math.PI * 0.1, 0, Math.PI * 2); ctx.fill()
  const wa = Math.sin(wingT) * 0.5
  ctx.save(); ctx.rotate(wa - 0.15); ctx.fillStyle = '#006fa0'; ctx.beginPath(); ctx.ellipse(0, 5, PARROT_R - 2, 9, 0.2, 0, Math.PI * 2); ctx.fill(); ctx.restore()
  ctx.fillStyle = CYAN; ctx.beginPath(); ctx.ellipse(0, 0, PARROT_R, PARROT_R - 3, 0, 0, Math.PI * 2); ctx.fill()
  ctx.save(); ctx.rotate(wa); ctx.fillStyle = '#55eeff'; ctx.beginPath(); ctx.ellipse(0, 4, PARROT_R - 6, 7, 0.15, 0, Math.PI * 2); ctx.fill(); ctx.restore()
  ctx.fillStyle = 'rgba(255,255,255,0.14)'; ctx.beginPath(); ctx.ellipse(3, 3, 9, 9, 0, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#22eeff'; ctx.beginPath(); ctx.arc(PARROT_R - 2, -PARROT_R + 4, 11, 0, Math.PI * 2); ctx.fill()
  const crestCol = hp > 3 ? PINK : hp > 1 ? GOLD : RED
  ctx.fillStyle = crestCol; ctx.shadowColor = crestCol; ctx.shadowBlur = 6
  ctx.beginPath(); ctx.moveTo(PARROT_R - 2, -PARROT_R - 7); ctx.lineTo(PARROT_R + 4, -PARROT_R); ctx.lineTo(PARROT_R - 8, -PARROT_R); ctx.fill()
  ctx.beginPath(); ctx.moveTo(PARROT_R - 5, -PARROT_R - 5); ctx.lineTo(PARROT_R + 1, -PARROT_R); ctx.lineTo(PARROT_R - 10, -PARROT_R); ctx.fill()
  ctx.shadowBlur = 0
  ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(PARROT_R + 5, -PARROT_R + 6, 4.5, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#04000a'; ctx.beginPath(); ctx.arc(PARROT_R + 6, -PARROT_R + 6, 2.5, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#fff'; ctx.beginPath(); ctx.arc(PARROT_R + 6.5, -PARROT_R + 5.5, 0.9, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = GOLD; ctx.beginPath(); ctx.moveTo(PARROT_R + 13, -PARROT_R + 6); ctx.lineTo(PARROT_R + 20, -PARROT_R + 9); ctx.lineTo(PARROT_R + 13, -PARROT_R + 12); ctx.fill()
  ctx.restore()
}

function drawZnake(ctx: CanvasRenderingContext2D, z: Znake) {
  if (z.dead) return
  const col = znakeColor(z.type); const isBoss = z.type === 'boss'; const flash = z.flashTimer > 0
  ctx.save()
  for (let i = z.segments.length - 1; i >= 0; i--) {
    const seg = z.segments[i]; const t = i / (z.segments.length - 1)
    const r = isBoss ? ZNAKE_SEGMENT_R * 1.7 * (1 - t * 0.4) : ZNAKE_SEGMENT_R * (1 - t * 0.35)
    ctx.shadowColor = flash ? '#fff' : col; ctx.shadowBlur = isBoss ? 14 : 8; ctx.fillStyle = flash ? '#fff' : col
    ctx.globalAlpha = 0.5 + 0.5 * (1 - t * 0.6); ctx.beginPath(); ctx.arc(seg.x, seg.y, r, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1
    if (i > 0 && i < z.segments.length - 1 && !flash) {
      ctx.fillStyle = 'rgba(0,0,0,0.3)'; ctx.beginPath(); ctx.arc(seg.x, seg.y, r * 0.5, 0, Math.PI * 2); ctx.fill()
      ctx.fillStyle = col; ctx.beginPath(); ctx.arc(seg.x - r * 0.2, seg.y - r * 0.2, r * 0.25, 0, Math.PI * 2); ctx.fill()
    }
  }
  ctx.shadowBlur = 0; ctx.globalAlpha = 1
  const head = z.segments[0]; const headR = isBoss ? ZNAKE_SEGMENT_R * 1.8 : ZNAKE_SEGMENT_R * 1.1; const hAngle = z.angle
  ctx.save(); ctx.translate(head.x, head.y); ctx.rotate(hAngle)
  ctx.strokeStyle = RED; ctx.lineWidth = isBoss ? 2.5 : 1.5; ctx.shadowColor = RED; ctx.shadowBlur = 4
  ctx.beginPath(); ctx.moveTo(headR, 0)
  const tongueFlick = Math.sin(Date.now() * 0.015) * 4
  ctx.lineTo(headR + 10 + tongueFlick, -4); ctx.moveTo(headR, 0); ctx.lineTo(headR + 10 - tongueFlick, 4); ctx.stroke(); ctx.shadowBlur = 0
  ctx.fillStyle = flash ? '#fff' : '#ff0'
  ctx.beginPath(); ctx.arc(headR * 0.2, -headR * 0.5, isBoss ? 4 : 2.5, 0, Math.PI * 2); ctx.fill()
  ctx.beginPath(); ctx.arc(headR * 0.2, headR * 0.5, isBoss ? 4 : 2.5, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = '#000'
  ctx.beginPath(); ctx.arc(headR * 0.3, -headR * 0.5, isBoss ? 2 : 1.2, 0, Math.PI * 2); ctx.fill()
  ctx.beginPath(); ctx.arc(headR * 0.3, headR * 0.5, isBoss ? 2 : 1.2, 0, Math.PI * 2); ctx.fill()
  if (isBoss) {
    ctx.fillStyle = GOLD; ctx.shadowColor = GOLD; ctx.shadowBlur = 10
    ctx.beginPath(); ctx.moveTo(-headR, -headR * 0.8); ctx.lineTo(-headR * 0.6, -headR * 1.5); ctx.lineTo(-headR * 0.2, -headR * 0.9); ctx.lineTo(headR * 0.2, -headR * 1.5); ctx.lineTo(headR * 0.5, -headR * 0.8); ctx.fill(); ctx.shadowBlur = 0
  }
  ctx.restore()
  if (isBoss || z.hp < z.maxHp) {
    const bw = isBoss ? 60 : 30; const bh = isBoss ? 6 : 3
    const bx = head.x - bw / 2; const by = head.y - headR - (isBoss ? 20 : 12)
    ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.beginPath(); ctx.roundRect(bx - 1, by - 1, bw + 2, bh + 2, 2); ctx.fill()
    ctx.fillStyle = '#333'; ctx.fillRect(bx, by, bw, bh)
    const pct = z.hp / z.maxHp; const hcol = pct > 0.5 ? GREEN : pct > 0.25 ? GOLD : RED
    ctx.fillStyle = hcol; ctx.shadowColor = hcol; ctx.shadowBlur = 4; ctx.fillRect(bx, by, bw * pct, bh); ctx.shadowBlur = 0
  }
  ctx.restore()
}

function drawBullet(ctx: CanvasRenderingContext2D, b: Bullet) {
  ctx.save(); ctx.shadowColor = CYAN; ctx.shadowBlur = 10; ctx.fillStyle = '#fff'
  ctx.beginPath(); ctx.arc(b.x, b.y, 4, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = CYAN; ctx.beginPath(); ctx.arc(b.x, b.y, 2.5, 0, Math.PI * 2); ctx.fill()
  ctx.globalAlpha = 0.3; ctx.fillStyle = CYAN
  ctx.beginPath(); ctx.arc(b.x - b.vx * 1.5, b.y - b.vy * 1.5, 3, 0, Math.PI * 2); ctx.fill()
  ctx.globalAlpha = 1; ctx.shadowBlur = 0; ctx.restore()
}

function drawEnemyBullet(ctx: CanvasRenderingContext2D, b: EnemyBullet) {
  ctx.save(); ctx.shadowColor = PURPLE; ctx.shadowBlur = 8; ctx.fillStyle = '#dd88ff'
  ctx.beginPath(); ctx.arc(b.x, b.y, 5, 0, Math.PI * 2); ctx.fill()
  ctx.fillStyle = PURPLE; ctx.beginPath(); ctx.arc(b.x, b.y, 3, 0, Math.PI * 2); ctx.fill()
  ctx.shadowBlur = 0; ctx.restore()
}

function updateGame(s: GameState, onGameOver: (score: number) => void): GameState {
  if (s.scene !== 'play') return s
  const g = { ...s }
  g.tick++; g.wingT += 0.2
  const { keys } = g
  const moveX = (keys['ArrowLeft'] || keys['KeyA'] ? -1 : 0) + (keys['ArrowRight'] || keys['KeyD'] ? 1 : 0)
  const moveY = (keys['ArrowUp'] || keys['KeyW'] ? -1 : 0) + (keys['ArrowDown'] || keys['KeyS'] ? 1 : 0)
  const movLen = Math.sqrt(moveX * moveX + moveY * moveY) || 1
  g.pvx = lerp(g.pvx, (moveX / movLen) * PARROT_SPEED * (moveX !== 0 ? 1 : 0), 0.2)
  g.pvy = lerp(g.pvy, (moveY / movLen) * PARROT_SPEED * (moveY !== 0 ? 1 : 0), 0.2)
  g.px = Math.max(PARROT_R, Math.min(W - PARROT_R, g.px + g.pvx))
  g.py = Math.max(PARROT_R, Math.min(H - PARROT_R, g.py + g.pvy))
  if (Math.abs(g.pvx) > 0.2) g.facing = { x: g.pvx > 0 ? 1 : -1, y: 0 }
  g.bulletCd = Math.max(0, g.bulletCd - 1)
  if (keys['Space'] && g.bulletCd === 0) {
    let aimVx = g.facing.x, aimVy = g.facing.y, nearest = Infinity
    for (const z of g.znakes) {
      if (z.dead) continue
      const d = dist(g.px, g.py, z.segments[0].x, z.segments[0].y)
      if (d < nearest) { nearest = d; const a = angleTo(g.px, g.py, z.segments[0].x, z.segments[0].y); aimVx = Math.cos(a); aimVy = Math.sin(a) }
    }
    g.bullets = [...g.bullets, { id: g.bulletId++, x: g.px + aimVx * (PARROT_R + 4), y: g.py + aimVy * (PARROT_R + 4), vx: aimVx * BULLET_SPEED, vy: aimVy * BULLET_SPEED, life: 80 }]
    g.bulletCd = BULLET_CD; burst(g.particles, g.px, g.py, CYAN, 3, 'feather')
  }
  const newBullets: Bullet[] = []
  for (const b of g.bullets) {
    b.x += b.vx; b.y += b.vy; b.life--
    if (b.life <= 0 || b.x < 0 || b.x > W || b.y < 0 || b.y > H) continue
    let hit = false
    for (const z of g.znakes) {
      if (z.dead) continue
      for (const seg of z.segments) {
        const hr = z.type === 'boss' ? ZNAKE_SEGMENT_R * 1.7 : ZNAKE_SEGMENT_R
        if (dist(b.x, b.y, seg.x, seg.y) < hr + 4) {
          z.hp--; z.flashTimer = 8; burst(g.particles, b.x, b.y, znakeColor(z.type), 6)
          if (z.hp <= 0) {
            z.dead = true; g.score += z.value * g.wave
            burst(g.particles, z.segments[0].x, z.segments[0].y, znakeColor(z.type), 20)
            burst(g.particles, z.segments[0].x, z.segments[0].y, '#fff', 10)
            g.floatingTexts = [...g.floatingTexts, { x: z.segments[0].x, y: z.segments[0].y, text: `+${z.value * g.wave}`, life: 1, col: z.type === 'boss' ? GOLD : CYAN, vy: -1.2 }]
          }
          hit = true; break
        }
      }
      if (hit) break
    }
    if (!hit) newBullets.push(b)
  }
  g.bullets = newBullets
  const newEBullets: EnemyBullet[] = []
  for (const b of g.enemyBullets) {
    b.x += b.vx; b.y += b.vy
    if (b.x < -10 || b.x > W + 10 || b.y < -10 || b.y > H + 10) continue
    if (dist(b.x, b.y, g.px, g.py) < PARROT_R + 4 && g.invTimer === 0) {
      g.hp--; g.invTimer = INVINCIBLE_FRAMES; g.damageFlash = DAMAGE_FLASH; burst(g.particles, g.px, g.py, PINK, 12)
      if (g.hp <= 0 && !g.submitted) {
        g.submitted = true; g.scene = 'game_over'
        if (g.score > g.best) g.best = g.score
        onGameOver(g.score); return g
      }
    } else { newEBullets.push(b) }
  }
  g.enemyBullets = newEBullets
  g.invTimer = Math.max(0, g.invTimer - 1); g.damageFlash = Math.max(0, g.damageFlash - 1)
  for (const z of g.znakes) {
    if (z.dead) continue; z.flashTimer = Math.max(0, z.flashTimer - 1)
    const head = z.segments[0]
    if (z.type === 'charger') {
      z.chargeTimer--
      if (!z.charging && z.chargeTimer <= 0) {
        z.charging = true; const a = angleTo(head.x, head.y, g.px, g.py)
        z.chargeVx = Math.cos(a) * z.speed * 5; z.chargeVy = Math.sin(a) * z.speed * 5; z.chargeTimer = 40
      }
      if (z.charging) {
        z.angle = Math.atan2(z.chargeVy, z.chargeVx); head.x += z.chargeVx; head.y += z.chargeVy; z.chargeTimer--
        if (z.chargeTimer <= 0) { z.charging = false; z.chargeTimer = 200 }
      } else {
        const target = angleTo(head.x, head.y, g.px, g.py); let diff = target - z.angle
        while (diff > Math.PI) diff -= Math.PI * 2; while (diff < -Math.PI) diff += Math.PI * 2
        z.angle += Math.sign(diff) * Math.min(Math.abs(diff), z.turnSpeed * 0.4)
        head.x += Math.cos(z.angle) * z.speed * 0.6; head.y += Math.sin(z.angle) * z.speed * 0.6
      }
    } else {
      const target = angleTo(head.x, head.y, g.px, g.py); let diff = target - z.angle
      while (diff > Math.PI) diff -= Math.PI * 2; while (diff < -Math.PI) diff += Math.PI * 2
      z.angle += Math.sign(diff) * Math.min(Math.abs(diff), z.turnSpeed)
      head.x += Math.cos(z.angle) * z.speed; head.y += Math.sin(z.angle) * z.speed
    }
    for (let i = 1; i < z.segments.length; i++) {
      const prev = z.segments[i - 1]; const seg = z.segments[i]
      const d = dist(seg.x, seg.y, prev.x, prev.y)
      if (d > ZNAKE_SEGMENT_GAP) { const a = angleTo(seg.x, seg.y, prev.x, prev.y); seg.x += Math.cos(a) * (d - ZNAKE_SEGMENT_GAP); seg.y += Math.sin(a) * (d - ZNAKE_SEGMENT_GAP) }
    }
    if (z.type === 'spitter' || z.type === 'boss') {
      z.spitTimer--
      if (z.spitTimer <= 0) {
        z.spitTimer = z.spitCd; const a = angleTo(head.x, head.y, g.px, g.py)
        const spd = z.type === 'boss' ? 4.5 : 3.2; const shots = z.type === 'boss' ? 3 : 1
        for (let s2 = 0; s2 < shots; s2++) {
          const spread = shots > 1 ? (s2 - 1) * 0.25 : 0
          g.enemyBullets = [...g.enemyBullets, { id: g.bulletId++, x: head.x, y: head.y, vx: Math.cos(a + spread) * spd, vy: Math.sin(a + spread) * spd }]
        }
      }
    }
    if (g.invTimer === 0) {
      for (const seg of z.segments) {
        const cr = z.type === 'boss' ? ZNAKE_SEGMENT_R * 1.7 : ZNAKE_SEGMENT_R
        if (dist(seg.x, seg.y, g.px, g.py) < PARROT_R + cr) {
          g.hp--; g.invTimer = INVINCIBLE_FRAMES; g.damageFlash = DAMAGE_FLASH; burst(g.particles, g.px, g.py, PINK, 14)
          if (g.hp <= 0 && !g.submitted) {
            g.submitted = true; g.scene = 'game_over'
            if (g.score > g.best) g.best = g.score
            onGameOver(g.score); return g
          }
          break
        }
      }
    }
  }
  g.znakes = g.znakes.filter(z => !z.dead)
  if (g.znakes.length === 0) {
    g.scene = 'wave_clear'; g.waveClearTimer = 120; g.score += 50 * g.wave
    burst(g.particles, W / 2, H / 2, GOLD, 30); burst(g.particles, W / 2, H / 2, CYAN, 20)
  }
  for (const p of g.particles) { p.x += p.vx; p.y += p.vy; if (p.type !== 'smoke') p.vy += 0.06; p.life -= p.decay }
  g.particles = g.particles.filter(p => p.life > 0)
  for (const ft of g.floatingTexts) { ft.y += ft.vy; ft.life -= 0.022 }
  g.floatingTexts = g.floatingTexts.filter(ft => ft.life > 0)
  return g
}

function updateWaveClear(s: GameState): GameState {
  if (s.scene !== 'wave_clear') return s
  const g = { ...s }; g.tick++; g.wingT += 0.2; g.waveClearTimer--
  if (g.tick % 8 === 0) burst(g.particles, rand(50, W - 50), rand(50, H - 50), [CYAN, PINK, GOLD][Math.floor(rand(0, 3))], 5)
  for (const p of g.particles) { p.x += p.vx; p.y += p.vy; p.vy += 0.05; p.life -= p.decay }
  g.particles = g.particles.filter(p => p.life > 0)
  if (g.waveClearTimer <= 0) {
    g.wave++; g.hp = Math.min(PARROT_MAX_HP, g.hp + 1); g.scene = 'play'
    const newZnakes = spawnWave(g.wave, g.znakeId); g.znakes = newZnakes; g.znakeId += newZnakes.length; g.enemyBullets = []
  }
  return g
}

function drawArena(ctx: CanvasRenderingContext2D, tick: number) {
  ctx.fillStyle = BG; ctx.fillRect(0, 0, W, H)
  ctx.strokeStyle = 'rgba(0,229,255,0.025)'; ctx.lineWidth = 1
  for (let x = 0; x <= W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke() }
  for (let y = 0; y <= H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke() }
  const pulse = 0.12 + 0.04 * Math.sin(tick * 0.04)
  ctx.strokeStyle = `rgba(0,229,255,${pulse})`; ctx.lineWidth = 3; ctx.strokeRect(2, 2, W - 4, H - 4)
  ctx.lineWidth = 1; ctx.strokeStyle = `rgba(0,229,255,${pulse * 0.5})`; ctx.strokeRect(6, 6, W - 12, H - 12)
}

function drawHUD(ctx: CanvasRenderingContext2D, g: GameState) {
  ctx.save()
  ctx.fillStyle = CYAN; ctx.font = `bold 13px ${MONO}`; ctx.textAlign = 'left'
  ctx.shadowColor = CYAN; ctx.shadowBlur = 5; ctx.fillText(`SCORE  ${g.score}`, 14, 22)
  ctx.textAlign = 'right'; ctx.fillText(`BEST  ${g.best}`, W - 14, 22); ctx.shadowBlur = 0
  ctx.textAlign = 'center'; ctx.fillStyle = GOLD; ctx.font = `bold 11px ${MONO}`
  ctx.shadowColor = GOLD; ctx.shadowBlur = 4; ctx.fillText(`WAVE ${g.wave}`, W / 2, 22); ctx.shadowBlur = 0
  const heartY = H - 18; ctx.font = `14px ${MONO}`; ctx.textAlign = 'left'
  for (let i = 0; i < PARROT_MAX_HP; i++) {
    const filled = i < g.hp; ctx.fillStyle = filled ? PINK : 'rgba(255,38,128,0.2)'
    if (filled) { ctx.shadowColor = PINK; ctx.shadowBlur = 6 }
    ctx.fillText('♥', 14 + i * 22, heartY); ctx.shadowBlur = 0
  }
  ctx.fillStyle = 'rgba(0,229,255,0.5)'; ctx.font = `10px ${MONO}`; ctx.textAlign = 'right'
  ctx.fillText(`ZNAKES: ${g.znakes.filter(z => !z.dead).length}`, W - 14, H - 18)
  ctx.restore()
}

// ─── Responsive Back Button CSS ───────────────────────────────────────────────
const BACK_BTN_CSS = `
  .zvs-back {
    position: fixed;
    top: clamp(10px, 3vw, 16px);
    left: clamp(10px, 3vw, 16px);
    z-index: 50;
    background: transparent;
    border: 1px solid rgba(0,229,255,0.25);
    color: rgba(0,229,255,0.55);
    font-family: 'JetBrains Mono','Courier New',monospace;
    font-size: clamp(8px, 2vw, 10px);
    letter-spacing: 2px;
    padding: clamp(5px,1.5vw,8px) clamp(10px,2.5vw,16px);
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
  }
  .zvs-back:hover {
    color: #00e5ff;
    border-color: #00e5ff;
    box-shadow: 0 0 12px rgba(0,229,255,0.2);
  }
  .zvs-canvas-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .zvs-canvas-wrap canvas {
    max-width: 100%;
    max-height: calc(100dvh - 80px);
    width: auto !important;
    height: auto !important;
    display: block;
    border: 1px solid rgba(0,229,255,0.2);
    box-shadow: 0 0 80px rgba(0,229,255,0.1);
    border-radius: 6px;
    touch-action: none;
    cursor: crosshair;
    object-fit: contain;
  }
`

export default function ParrotsVsZnakes() {
  const navigate = useNavigate()
  const { submitScore } = usePlayer()
  const submitScoreRef = useRef(submitScore)
  useEffect(() => { submitScoreRef.current = submitScore }, [submitScore])

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const gsRef = useRef<GameState>(makeInitialState())
  const rafRef = useRef<number>(0)

  // Inject CSS once
  useEffect(() => {
    const id = 'zvs-styles'
    if (document.getElementById(id)) return
    const el = document.createElement('style')
    el.id = id; el.textContent = BACK_BTN_CSS
    document.head.appendChild(el)
    return () => { document.getElementById(id)?.remove() }
  }, [])

  const onGameOver = useCallback((score: number) => {
    submitScoreRef.current('parrots-vs-znakes', score)
  }, [])

  const loop = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) { rafRef.current = requestAnimationFrame(loop); return }
    const ctx = canvas.getContext('2d')!
    let g = gsRef.current

    if (g.scene === 'play') g = updateGame(g, onGameOver)
    else if (g.scene === 'wave_clear') g = updateWaveClear(g)
    else g = { ...g, tick: g.tick + 1, wingT: g.wingT + 0.18 }
    gsRef.current = g

    drawArena(ctx, g.tick)

    for (const p of g.particles) {
      ctx.globalAlpha = Math.max(0, p.life) * (p.type === 'smoke' ? 0.4 : 0.9)
      ctx.fillStyle = p.col; ctx.shadowColor = p.col; ctx.shadowBlur = p.type === 'smoke' ? 0 : 4
      ctx.beginPath(); ctx.arc(p.x, p.y, p.size * Math.max(0.1, p.life), 0, Math.PI * 2); ctx.fill()
    }
    ctx.globalAlpha = 1; ctx.shadowBlur = 0

    for (const b of g.enemyBullets) drawEnemyBullet(ctx, b)
    for (const z of g.znakes) drawZnake(ctx, z)
    for (const b of g.bullets) drawBullet(ctx, b)

    const visible = g.invTimer === 0 || Math.floor(g.tick / 4) % 2 === 0
    if (visible && (g.scene === 'play' || g.scene === 'wave_clear' || g.scene === 'game_over')) {
      drawParrot(ctx, g.px, g.py, g.pvx < -0.3, g.wingT, g.damageFlash / DAMAGE_FLASH, g.hp)
    }

    for (const ft of g.floatingTexts) {
      ctx.globalAlpha = ft.life; ctx.fillStyle = ft.col; ctx.font = `bold 13px ${MONO}`; ctx.textAlign = 'center'
      ctx.shadowColor = ft.col; ctx.shadowBlur = 6; ctx.fillText(ft.text, ft.x, ft.y); ctx.shadowBlur = 0
    }
    ctx.globalAlpha = 1; drawHUD(ctx, g)

    if (g.scene === 'menu') {
      ctx.fillStyle = 'rgba(4,0,10,0.82)'; ctx.fillRect(0, 0, W, H); ctx.textAlign = 'center'
      ctx.fillStyle = CYAN; ctx.font = `900 11px ${MONO}`; ctx.shadowColor = CYAN; ctx.shadowBlur = 14
      ctx.fillText('10K SQUADS PRESENTS', W / 2, H / 2 - 110); ctx.shadowBlur = 0
      ctx.fillStyle = '#fff'; ctx.font = `900 34px 'Arial Black',sans-serif`; ctx.shadowColor = CYAN; ctx.shadowBlur = 20
      ctx.fillText('PARROTS', W / 2, H / 2 - 68); ctx.shadowBlur = 0
      ctx.fillStyle = PINK; ctx.font = `900 13px ${MONO}`; ctx.shadowColor = PINK; ctx.shadowBlur = 10
      ctx.fillText('VS', W / 2, H / 2 - 46); ctx.shadowBlur = 0
      ctx.fillStyle = GREEN; ctx.font = `900 34px 'Arial Black',sans-serif`; ctx.shadowColor = GREEN; ctx.shadowBlur = 20
      ctx.fillText('ZNAKES', W / 2, H / 2 - 12); ctx.shadowBlur = 0
      ctx.fillStyle = 'rgba(255,255,255,0.45)'; ctx.font = `10px ${MONO}`
      ctx.fillText('WASD / ↑↓←→  move', W / 2, H / 2 + 28)
      ctx.fillText('SPACE  shoot  (auto-aims nearest znake)', W / 2, H / 2 + 44)
      ctx.fillStyle = GREEN; ctx.font = `9px ${MONO}`; ctx.fillText('● crawler  ', W / 2 - 90, H / 2 + 62)
      ctx.fillStyle = PURPLE; ctx.fillText('● spitter  ', W / 2 - 15, H / 2 + 62)
      ctx.fillStyle = RED; ctx.fillText('● charger  ', W / 2 + 55, H / 2 + 62)
      ctx.fillStyle = GOLD; ctx.font = `9px ${MONO}`; ctx.fillText('★ boss', W / 2, H / 2 + 76)
      const blink = Math.sin(g.tick * 0.08) > 0
      if (blink) { ctx.fillStyle = PINK; ctx.font = `bold 13px ${MONO}`; ctx.shadowColor = PINK; ctx.shadowBlur = 10; ctx.fillText('PRESS SPACE TO START', W / 2, H / 2 + 106); ctx.shadowBlur = 0 }
      drawParrot(ctx, W / 2, H / 2 + 148, false, g.wingT, 0, PARROT_MAX_HP)
    }

    if (g.scene === 'wave_clear') {
      ctx.fillStyle = 'rgba(4,0,10,0.55)'; ctx.fillRect(0, 0, W, H); ctx.textAlign = 'center'
      ctx.fillStyle = GOLD; ctx.font = `900 28px 'Arial Black',sans-serif`; ctx.shadowColor = GOLD; ctx.shadowBlur = 20
      ctx.fillText(`WAVE ${g.wave} CLEARED!`, W / 2, H / 2 - 20); ctx.shadowBlur = 0
      ctx.fillStyle = CYAN; ctx.font = `11px ${MONO}`; ctx.fillText(`+${50 * g.wave} BONUS`, W / 2, H / 2 + 8)
      const prog = 1 - g.waveClearTimer / 120
      ctx.fillStyle = 'rgba(0,229,255,0.2)'; ctx.fillRect(W / 2 - 60, H / 2 + 24, 120 * prog, 4)
    }

    if (g.scene === 'game_over') {
      ctx.fillStyle = 'rgba(4,0,10,0.9)'; ctx.fillRect(0, 0, W, H); ctx.textAlign = 'center'
      ctx.fillStyle = RED; ctx.font = `900 42px 'Arial Black',sans-serif`; ctx.shadowColor = RED; ctx.shadowBlur = 30
      ctx.fillText('SPLATTED', W / 2, H / 2 - 80); ctx.shadowBlur = 0
      ctx.fillStyle = 'rgba(255,255,255,0.3)'; ctx.font = `11px ${MONO}`; ctx.fillText('GAME OVER', W / 2, H / 2 - 50)
      ctx.fillStyle = CYAN; ctx.font = `bold 11px ${MONO}`; ctx.fillText(`WAVE  ${g.wave}`, W / 2, H / 2 - 28)
      ctx.fillStyle = '#fff'; ctx.font = `bold 30px ${MONO}`; ctx.shadowColor = CYAN; ctx.shadowBlur = 12
      ctx.fillText(`${g.score} PTS`, W / 2, H / 2 + 2); ctx.shadowBlur = 0
      if (g.score >= g.best && g.score > 0) {
        ctx.fillStyle = GOLD; ctx.font = `bold 12px ${MONO}`; ctx.shadowColor = GOLD; ctx.shadowBlur = 8
        ctx.fillText('★ NEW BEST ★', W / 2, H / 2 + 26); ctx.shadowBlur = 0
      } else {
        ctx.fillStyle = 'rgba(0,229,255,0.4)'; ctx.font = `10px ${MONO}`; ctx.fillText(`BEST: ${g.best}`, W / 2, H / 2 + 26)
      }
      const blink = Math.sin(g.tick * 0.08) > 0
      if (blink) { ctx.fillStyle = CYAN; ctx.font = `bold 12px ${MONO}`; ctx.shadowColor = CYAN; ctx.shadowBlur = 6; ctx.fillText('PRESS SPACE TO RETRY', W / 2, H / 2 + 64); ctx.shadowBlur = 0 }
    }

    rafRef.current = requestAnimationFrame(loop)
  }, [onGameOver])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      gsRef.current.keys[e.code] = true
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault()
      if (e.code === 'Space') {
        const scene = gsRef.current.scene
        if (scene === 'menu' || scene === 'game_over') gsRef.current = startGame(gsRef.current)
      }
    }
    const onKeyUp = (e: KeyboardEvent) => { gsRef.current.keys[e.code] = false }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)

    const canvas = canvasRef.current
    let touchId: number | null = null, touchOriginX = 0, touchOriginY = 0
    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault()
      const s = gsRef.current
      if (s.scene === 'menu' || s.scene === 'game_over') { gsRef.current = startGame(s); return }
      const t = e.changedTouches[0]; touchId = t.identifier; touchOriginX = t.clientX; touchOriginY = t.clientY
      gsRef.current.keys['Space'] = true; setTimeout(() => { gsRef.current.keys['Space'] = false }, 80)
    }
    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault()
      for (let i = 0; i < e.changedTouches.length; i++) {
        const t = e.changedTouches[i]; if (t.identifier !== touchId) continue
        const dx = t.clientX - touchOriginX, dy = t.clientY - touchOriginY, threshold = 18
        gsRef.current.keys['ArrowLeft'] = dx < -threshold; gsRef.current.keys['ArrowRight'] = dx > threshold
        gsRef.current.keys['ArrowUp'] = dy < -threshold; gsRef.current.keys['ArrowDown'] = dy > threshold
        if (Math.sqrt(dx * dx + dy * dy) > threshold) { gsRef.current.keys['Space'] = true; setTimeout(() => { gsRef.current.keys['Space'] = false }, 60) }
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      e.preventDefault()
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === touchId) {
          touchId = null
          const k = gsRef.current.keys
          k['ArrowLeft'] = false; k['ArrowRight'] = false; k['ArrowUp'] = false; k['ArrowDown'] = false
        }
      }
    }
    canvas?.addEventListener('touchstart', onTouchStart, { passive: false })
    canvas?.addEventListener('touchmove', onTouchMove, { passive: false })
    canvas?.addEventListener('touchend', onTouchEnd, { passive: false })
    rafRef.current = requestAnimationFrame(loop)
    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      canvas?.removeEventListener('touchstart', onTouchStart)
      canvas?.removeEventListener('touchmove', onTouchMove)
      canvas?.removeEventListener('touchend', onTouchEnd)
    }
  }, [loop])

  return (
    <div style={{ minHeight: '100dvh', background: BG, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, userSelect: 'none', WebkitUserSelect: 'none', overflow: 'hidden' }}>
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10, backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.055) 2px,rgba(0,0,0,0.055) 4px)' }} />

      {/* Responsive back button */}
      <button className="zvs-back" onClick={() => { cancelAnimationFrame(rafRef.current); navigate('/games') }}>
        ← GAMES
      </button>

      <div style={{ fontSize: 'clamp(7px,2vw,9px)', color: 'rgba(0,229,255,0.4)', letterSpacing: 3, marginBottom: 8 }}>
        10K SQUADS · PARROTS VS ZNAKES
      </div>

      <div className="zvs-canvas-wrap" style={{ width: '100%', maxWidth: W + 'px', padding: '0 8px' }}>
        <canvas ref={canvasRef} width={W} height={H} style={{ touchAction: 'none', cursor: 'crosshair' }} />
      </div>

      <div style={{ marginTop: 10, fontSize: 'clamp(7px,2vw,9px)', color: 'rgba(255,255,255,0.15)', letterSpacing: 2, textAlign: 'center', padding: '0 16px' }}>
        WASD / ARROWS · SPACE shoot
      </div>
    </div>
  )
}